import NotificationBadge from '@/components/raytd/notification-badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { ChevronDown, FileQuestion } from 'lucide-react'
import { TestCounts } from '../utils/entities'
import CountPill from './excluded-count-pill'

interface ExcludedTestsBadgeProps {
    counts: TestCounts
    onClick?: () => void
    isActive?: boolean
}

export function ExcludedTestsBadge({ counts, onClick, isActive }: ExcludedTestsBadgeProps) {
    const excludedCount = counts?.total - counts?.included;
    //    if (excludedCount <= 0) return null

    const hasExcluded = excludedCount > 0;
    const buttonTextColor = isActive ? 'text-white' : 'text-zinc-950';
    const activeStyles = isActive ? 'bg-tests-status border-tests-status' : 'bg-white';

    return (
        <div className="flex items-center">
            <Popover>
                <NotificationBadge
                    className="bg-tests-status text-white font-normal -right-1 -top-1 z-10"
                    label={excludedCount}
                    show={hasExcluded}>
                    <Button
                        aria-label="Excluded Tests Action"
                        variant="outline"
                        onClick={onClick}
                        className={cn(
                            "flex flex-row items-center gap-1 rounded-r-none border-r-0",
                            buttonTextColor,
                            activeStyles
                        )}
                    >
                        <FileQuestion className="h-4 w-4" />
                        Excluded Tests
                    </Button>
                </NotificationBadge>

                <PopoverTrigger asChild>
                    <Button
                        aria-label="Show Excluded Tests Details"
                        variant="outline"
                        className={cn(
                            "px-2 rounded-l-none",

                            'bg-white'
                        )}
                    >
                        <ChevronDown className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>

                <PopoverContent className="bg-white border border-zinc-200 p-4 shadow-lg w-96" align='end'>
                    <ExcludedTestsBanner tests={counts} vertical />
                </PopoverContent>
            </Popover>
        </div>
    )
}

interface ExcludedTestsBannerProps {
    tests: TestCounts;
    vertical?: boolean;
    text?: string;
    showEmpty?: boolean;
}

export function ExcludedTestsBanner({
    tests,
    vertical = false,
    text = 'Excluded Tests are not included in data analysis.',
    showEmpty = true
}: ExcludedTestsBannerProps) {
    return (
        <div className="">
            <div className="flex justify-between items-center bg-orange-400/25 my-4 p-4 rounded-md">
                <div className="flex-1 flex flex-row justify-between items-center">
                    <div className="text-sm text-foreground">{text}</div>
                    {!vertical && (
                        <div className="flex flex-row gap-1 items-end justify-center">

                            <CountPill count={tests?.missingData ?? 0} type="missing" />
                            <CountPill count={tests?.fastFill ?? 0} type="fastfill" />
                            <CountPill count={tests?.status} type="status" />
                            <CountPill count={0} type="excluded" />
                            <div className="w-10 flex items-end justify-end">
                            </div>

                        </div>
                    )}
                </div>
            </div>

            {
                vertical && (
                    <div className="flex flex-col space-y-3 text-sm">
                        <div className={cn("flex flex-row gap-3 items-center",
                            !showEmpty && (tests?.missingData ?? 0) === 0 && 'hidden'
                        )}>
                            <CountPill count={tests?.missingData ?? 0} type="missing" className="flex-shrink-0" />
                            <div>The Test includes compulsory data fields that have not been completed.</div>
                        </div>
                        <div className={cn("flex flex-row gap-3 items-center",
                            !showEmpty && (tests?.fastFill ?? 0) === 0 && 'hidden'
                        )}>
                            <CountPill count={tests?.fastFill ?? 0} type="fastfill" className="flex-shrink-0" />
                            <div>The Test includes text that does not match a pre-approved Fast Fill response.</div>
                        </div>
                        <div className={cn("flex flex-row gap-3 items-center",
                            !showEmpty && (tests?.status ?? 0) === 0 && 'hidden'
                        )}>
                            <CountPill count={tests?.status} type="status" className="flex-shrink-0" />
                            <div>The Test is currently in Archived, Draft or QA status and has not been saved as Final.</div>
                        </div>
                        <div className={cn("flex flex-row gap-3 items-center",
                            !showEmpty && (tests?.manuallyExcluded ?? 0) === 0 && 'hidden'
                        )}>
                            <CountPill count={tests?.manuallyExcluded} type="excluded" className="flex-shrink-0" />
                            <div className="flex-1">The Test has been manually excluded from publishing by an Author of this report.</div>
                        </div>
                    </div>
                )
            }

        </div>

    )
}